import React, { useState } from 'react';
import {
  useGetList,
  useCreate,
  useUpdate,
  useDelete,
  useNotify,
} from 'react-admin';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

interface CompanyCvor {
  id: number;
  cvorNscNumber: string;
  legalOperatingName: string;
  active?: boolean;
  equipmentCount?: number;
}

interface CvorDraft {
  cvorNscNumber: string;
  legalOperatingName: string;
  active: boolean;
}

const emptyDraft = (): CvorDraft => ({ cvorNscNumber: '', legalOperatingName: '', active: true });

const draftFromCvor = (cvor: CompanyCvor): CvorDraft => ({
  cvorNscNumber: cvor.cvorNscNumber || '',
  legalOperatingName: cvor.legalOperatingName || '',
  active: cvor.active !== false,
});

const formatCvorNscNumber = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const CompanyCvorsSection = () => {
  const notify = useNotify();
  const { data: cvors = [], isLoading, refetch } = useGetList<CompanyCvor>('cvors', {
    pagination: { page: 1, perPage: 50 },
    sort: { field: 'id', order: 'ASC' },
    filter: { includeInactive: true },
  });
  const [createCvor, { isLoading: creating }] = useCreate();
  const [updateCvor, { isLoading: updating }] = useUpdate();
  const [deleteCvor, { isLoading: deleting }] = useDelete();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CompanyCvor | null>(null);
  const [draft, setDraft] = useState<CvorDraft>(emptyDraft());
  const [deleteTarget, setDeleteTarget] = useState<CompanyCvor | null>(null);

  const saving = creating || updating;

  const openCreate = () => {
    setEditing(null);
    setDraft(emptyDraft());
    setDialogOpen(true);
  };

  const openEdit = (cvor: CompanyCvor) => {
    setEditing(cvor);
    setDraft(draftFromCvor(cvor));
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (!draft.cvorNscNumber.trim()) {
      notify('Enter a CVOR/NSC number', { type: 'warning' });
      return;
    }
    if (!draft.legalOperatingName.trim()) {
      notify('Enter a legal operating name', { type: 'warning' });
      return;
    }

    const payload = {
      cvorNscNumber: draft.cvorNscNumber.trim(),
      legalOperatingName: draft.legalOperatingName.trim(),
      active: draft.active,
    };

    const onSuccess = () => {
      notify(editing ? 'CVOR updated' : 'CVOR added', { type: 'success' });
      setDialogOpen(false);
      refetch();
    };
    const onError = () => {
      notify(editing ? 'Failed to update CVOR' : 'Failed to add CVOR', { type: 'error' });
    };

    if (editing) {
      updateCvor('cvors', { id: editing.id, data: payload, previousData: editing }, { onSuccess, onError });
    } else {
      createCvor('cvors', { data: payload }, { onSuccess, onError });
    }
  };

  const patchCvor = (cvor: CompanyCvor, data: Partial<CompanyCvor>, successMessage: string) => {
    updateCvor('cvors', { id: cvor.id, data, previousData: cvor }, {
      onSuccess: () => {
        notify(successMessage, { type: 'success' });
        refetch();
      },
      onError: () => notify('Failed to update CVOR', { type: 'error' }),
    });
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteCvor('cvors', { id: deleteTarget.id, previousData: deleteTarget }, {
      onSuccess: () => {
        notify('CVOR deleted', { type: 'success' });
        setDeleteTarget(null);
        refetch();
      },
      onError: () => notify('This CVOR has equipment assigned and cannot be deleted. Deactivate it instead.', { type: 'error' }),
    });
  };

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Each CVOR/NSC has its own registered legal name. The first CVOR you add is automatically
        assigned to any vehicle that doesn't already have one.
      </Typography>

      {isLoading ? (
        <Typography variant="body2" color="text.secondary">Loading CVORs…</Typography>
      ) : cvors.length === 0 ? (
        <Typography variant="body2" sx={{ mb: 2 }}>
          No CVORs yet. Add one to start assigning it to vehicles.
        </Typography>
      ) : (
        <Box sx={{ mb: 2 }}>
          {cvors.map((cvor) => (
            <Box
              key={cvor.id}
              sx={{
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body1">{cvor.cvorNscNumber}</Typography>
                  {cvor.active === false && <Chip label="Inactive" size="small" />}
                  <Chip label={`${cvor.equipmentCount ?? 0} vehicle(s)`} size="small" variant="outlined" />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {cvor.legalOperatingName}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  onClick={() => patchCvor(cvor, { active: cvor.active === false }, cvor.active === false ? 'CVOR activated' : 'CVOR deactivated')}
                >
                  {cvor.active === false ? 'Activate' : 'Deactivate'}
                </Button>
                <Button size="small" onClick={() => openEdit(cvor)}>
                  Edit
                </Button>
                {(cvor.equipmentCount ?? 0) === 0 && (
                  <Button size="small" color="error" onClick={() => setDeleteTarget(cvor)}>
                    Delete
                  </Button>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Button variant="outlined" startIcon={<AddIcon />} onClick={openCreate}>
        Add CVOR
      </Button>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm" disableEnforceFocus>
        <DialogTitle>{editing ? 'Edit CVOR' : 'Add CVOR'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="CVOR/NSC Number"
            value={draft.cvorNscNumber}
            onChange={(e) => setDraft((prev) => ({ ...prev, cvorNscNumber: formatCvorNscNumber(e.target.value) }))}
            placeholder="123-456-789"
            helperText="9-digit CVOR (Ontario) or NSC (rest of Canada) number"
            inputProps={{ maxLength: 11 }}
            fullWidth
            required
          />
          <TextField
            label="Legal Operating Name"
            value={draft.legalOperatingName}
            onChange={(e) => setDraft((prev) => ({ ...prev, legalOperatingName: e.target.value }))}
            fullWidth
            required
          />
          <FormControlLabel
            control={
              <Switch
                checked={draft.active}
                onChange={(e) => setDraft((prev) => ({ ...prev, active: e.target.checked }))}
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete CVOR?</DialogTitle>
        <DialogContent>
          <Typography>
            {deleteTarget?.cvorNscNumber} has no vehicles assigned and will be permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
